import {
  Box,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Dialog,
  Divider,
  DialogContent,
  Button,
  TableCell,
  TableRow,
  TableContainer,
  Table,
  TableBody,
  DialogActions,
  Paper,
  Stack,
  TableHead,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import "@fontsource/barlow-semi-condensed/600";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import React, { useEffect, useState } from "react";
import GplanIconWhite from "../../../common-components/icons/GplanIconWhite";
import CancelIcon from "@mui/icons-material/Cancel";
import MenuIcon from "@mui/icons-material/Menu"; // For the hamburger menu icon
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../axiosInstance";
import { showToast } from "../../../redux/features/toast/toast.slice";
import { useDispatch } from "react-redux";
import { AppDispatch, persistor } from "../../../redux/store";
import { reset as resetGraph } from "../../../redux/features/graph/graph.slice";
import { resetStates } from "../../../redux/features/appState/appState.slice";
import { clearAuthSession } from "../../../utils/auth";

interface Subscription {
  id: number;
  product_name: string;
  next_billing_date: string;
  previous_billing_date: string;
  to_be_cancelled: boolean;
}

interface NavbarProps {
  showBackToHome?: boolean;
  onBackToHome?: () => void;
}

export default function Navbar({
  showBackToHome = false,
  onBackToHome,
}: NavbarProps) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate(); // Hook for navigation

  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fetched, setfetched] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedPlanName, setSelectedPlanName] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [trialInfo, setTrialInfo] = useState<{ onTrial: boolean; daysLeft: number }>({ onTrial: false, daysLeft: 0 });

  const toggleCancelStatus = (id: number, value: boolean) => {
    setSubscriptions((prevSubs) =>
      prevSubs.map((sub) =>
        sub.id === id ? { ...sub, to_be_cancelled: value } : sub,
      ),
    );
  };

  const openConfirm = (id: number, name: string) => {
    setSelectedPlanId(id);
    setSelectedPlanName(name);
    setConfirmOpen(true);
  };

  const handleConfirmUnsubscribe = async () => {
    try {
      const res = await axiosInstance.post<any>(
        `${process.env.REACT_APP_BASEURL}/users/dodopayments/unsubscribe/`,
        { subscription_id: selectedPlanId },
      );
      dispatch(showToast({ message: res.data.message, type: "success" }));
      toggleCancelStatus(res.data.subscription_id, true);
    } catch (err) {
      dispatch(showToast({ message: "Error cancelling subscription.", type: "error" }));
      console.error("Error fetching subscriptions:", err);
    }
    setConfirmOpen(false);
    setSelectedPlanId(null);
    setSelectedPlanName(null);
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return "—";
    // prefer using split so we don't depend on locale/timezone
    const datePart = String(iso).split("T")[0];
    return datePart || "—";
  };

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const res = await axiosInstance.get<{ subscriptions: Subscription[] }>(
          `${process.env.REACT_APP_BASEURL}/users/subscriptions/active/`,
        );
        setSubscriptions(res.data.subscriptions);
      } catch (err) {
        setfetched(false);
      }
    };

    const fetchTrialInfo = async () => {
      try {
        const res = await axiosInstance.get<{ subscriptions: any[] }>(
          `${process.env.REACT_APP_BASEURL}/users/subscriptions/status/`,
        );
        const subs = res.data?.subscriptions ?? [];
        const trialEntry = subs.find((s: any) => s.on_trial === true);
        if (trialEntry) {
          setTrialInfo({ onTrial: true, daysLeft: trialEntry.days_left ?? 0 });
        }
      } catch (err) { }
    };

    fetchSubscriptions();
    fetchTrialInfo();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Function to handle logout
  const handleLogout = () => {
    dispatch(resetGraph());
    dispatch(resetStates());
    sessionStorage.clear();
    persistor.purge();
    clearAuthSession();
    navigate("/"); // Redirect to the home page
  };

  return (
    <>
      <div className="homepage-navbar-outerdiv">
        <Grid container alignItems="center" justifyContent="space-between">
          {/* Left corner with dropdown */}
          <Box>
            {showBackToHome ? (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Back to home"
                onClick={onBackToHome}
                style={{ margin: "0 0 0 3em" }}
              >
                <ArrowBackIcon style={{ color: "#F2F2F2" }} />
              </IconButton>
            ) : (
              <>
                {/* Hamburger menu is intentionally kept for non-Saved Documents pages. */}
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-controls="home-menu"
                  aria-haspopup="true"
                  onClick={handleMenuClick}
                  style={{ margin: "0 0 0 3em" }}
                >
                  <MenuIcon style={{ color: "#F2F2F2" }} />
                </IconButton>

                <Menu
                  id="home-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate("/newinputeditor");
                    }}
                  >
                    New Input Editor
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate("/plans");
                    }}
                  >
                    Plans
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate("/api-keys");
                    }}
                  >
                    API Keys
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {/* Center with logo and GPLAN text */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              margin: "30px",
              marginLeft: "72px",
            }}
          >
            <GplanIconWhite />
            <Typography
              sx={{
                textAlign: "left",
                fontFamily: "Barlow Semi Condensed",
                fontSize: "20px",
                fontStyle: "normal",
                color: "#F2F2F2",
                fontWeight: 500,
                lineHeight: "normal",
                padding: "8px",
              }}
            >
              GPLAN
            </Typography>
          </Box>

          {/* Right corner with a user icon */}
          <Box sx={{ marginRight: "72px" }}>
            <IconButton onClick={() => setOpen(true)} sx={{ color: "#111" }}>
              <Avatar
                sx={{
                  bgcolor: "#FBE5C6",
                  color: "#000",
                  boxShadow: 1,
                  border: "2px solid #FF9A56", // navy accent around avatar
                }}
              >
                {String(localStorage.getItem("name") || "U").charAt(0)}
              </Avatar>
            </IconButton>

            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  minWidth: 360,
                  maxWidth: 620,
                  bgcolor: "transparent",
                  boxShadow: "none",
                  p: 0,
                },
              }}
              BackdropProps={{
                sx: {
                  backgroundColor: "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(6px)", // increased blur
                },
              }}
            >
              {/* Card-like container */}
              <Paper
                elevation={10}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  p: 2,
                  bgcolor: "#FBE5C6",
                  color: "#111",
                  mx: 1,
                }}
              >
                {/* Header: avatar + user info */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "#FF9A56", // navy avatar background
                      color: "#FBE5C6",
                      width: 56,
                      height: 56,
                      fontSize: 22,
                      // boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                    }}
                  >
                    {String(localStorage.getItem("name") || "U").charAt(0)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        letterSpacing: "-0.2px",
                      }}
                    >
                      {localStorage.getItem("name") || "—"}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 0.4 }}
                    >
                      <EmailOutlinedIcon
                        sx={{
                          width: 16,
                          height: 16,
                          color: "rgba(0,0,0,0.45)",
                        }}
                      />
                      <Typography
                        noWrap
                        variant="body2"
                        sx={{ color: "rgba(0,0,0,0.6)", fontSize: 13 }}
                      >
                        {localStorage.getItem("email") || "—"}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Divider sx={{ borderColor: "rgba(0,0,0,0.06)", my: 1 }} />

                {subscriptions && subscriptions.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ boxShadow: "none" }}
                  >
                    <Table size="small" aria-label="plans table">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              textAlign: "center",
                            }}
                          >
                            Active Plan(s)
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              textAlign: "center",
                            }}
                          >
                            Start
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              textAlign: "center",
                            }}
                          >
                            End
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              textAlign: "center",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {subscriptions.map((sub: Subscription, idx: number) => {
                          const start = formatDate(sub.previous_billing_date);
                          const end = formatDate(sub.next_billing_date);

                          return (
                            <TableRow key={`${sub.id}-${idx}`}>
                              <TableCell
                                sx={{
                                  fontSize: 13,
                                  maxWidth: 160,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {sub.product_name || `Plan ${idx + 1}`}
                              </TableCell>

                              <TableCell
                                sx={{ fontSize: 13, textAlign: "center" }}
                              >
                                {start}
                              </TableCell>

                              <TableCell
                                sx={{ fontSize: 13, textAlign: "center" }}
                              >
                                {end}
                              </TableCell>

                              <TableCell sx={{ textAlign: "right" }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="space-evenly"
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      openConfirm(sub.id, sub.product_name)
                                    }
                                    startIcon={
                                      sub.to_be_cancelled ? (
                                        <CancelIcon />
                                      ) : (
                                        <WarningAmberIcon />
                                      )
                                    }
                                    sx={{
                                      width: "100%",
                                      textTransform: "none",
                                      borderColor: "rgba(0,0,0,0.08)",
                                      color: "rgba(0,0,0,0.8)",
                                      "&:hover": {
                                        borderColor: "rgba(0,0,0,0.12)",
                                      },
                                    }}
                                    disabled={sub.to_be_cancelled}
                                  >
                                    {sub.to_be_cancelled
                                      ? "Canceled"
                                      : "Unsubscribe"}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : trialInfo.onTrial ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "rgba(28, 76, 130, 0.06)",
                      borderRadius: 2,
                      px: 2.5,
                      py: 1.5,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#FF9A56" }}>
                        Free Trial
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.55)", mt: 0.25 }}>
                        {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? "day" : "days"} remaining
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate("/plans")}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        borderColor: "#FF9A56",
                        color: "#FF9A56",
                        "&:hover": { bgcolor: "rgba(28, 76, 130, 0.08)" },
                      }}
                    >
                      View Plans
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 50,
                      fontSize: 13,
                      color: fetched === false ? "red" : "rgba(0,0,0,0.5)",
                      gap: 1,
                    }}
                  >
                    {fetched === false ? (
                      <>
                        <WarningAmberIcon sx={{ color: "red", fontSize: 18 }} />
                        <span>Unable to fetch</span>
                      </>
                    ) : (
                      <span>— no active plans —</span>
                    )}
                  </Box>
                )}

                <Divider sx={{ borderColor: "rgba(0,0,0,0.06)", my: 2 }} />

                {/* Actions */}
                <DialogActions sx={{ px: 0, justifyContent: "space-between" }}>
                  <Box sx={{ pl: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setOpen(false)}
                      sx={{ textTransform: "none", color: "rgba(0,0,0,0.7)" }}
                    >
                      Close
                    </Button>
                  </Box>

                  <Box sx={{ pr: 1 }}>
                    <Button
                      onClick={() => {
                        setOpen(false);
                        handleLogout();
                      }}
                      startIcon={<LogoutIcon />}
                      variant="contained"
                      sx={{
                        bgcolor: "#ff4c4c",
                        "&:hover": { bgcolor: "#d32f2f" },
                        textTransform: "none",
                        // boxShadow: "0 6px 18px rgba(255,76,76,0.18)",
                      }}
                    >
                      Logout
                    </Button>
                  </Box>
                </DialogActions>
              </Paper>
            </Dialog>

            {/* Confirmation dialog for unsubscribe */}
            <Dialog
              open={confirmOpen}
              onClose={() => {
                setConfirmOpen(false);
                setSelectedPlanId(null);
                setSelectedPlanName(null);
              }}
              PaperProps={{ sx: { borderRadius: 2, p: 2, minWidth: 320 } }}
              BackdropProps={{ sx: { backdropFilter: "blur(2px)" } }}
            >
              <DialogContent
                sx={{ display: "flex", gap: 2, alignItems: "center" }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#FBE5C6",
                    color: "#FF9A56",
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <WarningAmberIcon />
                </Avatar>

                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    Confirm unsubscribe
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(0,0,0,0.65)", mt: 0.5 }}
                  >
                    Are you sure you want to unsubscribe from{" "}
                    <strong>
                      {selectedPlanId !== null
                        ? selectedPlanName
                        : "Unable to fetch selected Plan"}
                    </strong>
                    ?
                  </Typography>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => {
                    setConfirmOpen(false);
                    setSelectedPlanId(null);
                    setSelectedPlanName(null);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleConfirmUnsubscribe}
                  variant="contained"
                  sx={{
                    bgcolor: "#ff4c4c",
                    "&:hover": { bgcolor: "#d32f2f" },
                    textTransform: "none",
                  }}
                >
                  Yes, unsubscribe
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Grid>
      </div>
    </>
  );
}
